// Indicatieve terugbetalingsgegevens per Belgisch ziekenfonds voor de publieke
// /terugbetaling-pagina. Dit is een presentatie-kopie van
// speakhere-api/src/data/mutualiteiten.ts — de bedragen betreffen de
// AANVULLENDE verzekering van het ziekenfonds (niet de RIZIV-conventie) en
// zijn indicatief: ze verschillen regionaal en wijzigen jaarlijks.
// HOUD IN SYNC met de API-seed en werk REIMB_VERIFIED_DATE bij na herverificatie.

export type Lang = "nl" | "fr" | "en";

/** Datum waarop de bedragen voor het laatst tegen de fondsvoorwaarden zijn
 *  gecontroleerd — getoond als "geverifieerd op …" bij elke tabel. */
export const REIMB_VERIFIED_DATE: Record<Lang, string> = {
  nl: "2 juli 2026",
  fr: "2 juillet 2026",
  en: "2 July 2026",
};

export interface MutualiteitRow {
  code: string;
  name: string;
  /** Korte indicatie van het bedrag per sessie, per taal. */
  amount: Record<Lang, string>;
  /** Sessies/plafond, per taal. */
  cap: Record<Lang, string>;
  /** Voorwaarden en bijzonderheden, per taal. */
  conditions: Record<Lang, string>;
  infoUrl?: string;
}

export const MUTUALITEITEN_INFO: MutualiteitRow[] = [
  {
    code: "cm",
    name: "CM (Christelijke Mutualiteit)",
    amount: {
      nl: "tot € 20/sessie (€ 30 bij verhoogde tegemoetkoming)",
      fr: "jusqu'à 20 €/séance (30 € en intervention majorée)",
      en: "up to €20/session (€30 with increased allowance)",
    },
    cap: {
      nl: "tot 20 sessies per kalenderjaar (regeling vernieuwd op 1 januari 2026)",
      fr: "jusqu'à 20 séances par année civile (régime renouvelé au 1er janvier 2026)",
      en: "up to 20 sessions per calendar year (scheme renewed on 1 January 2026)",
    },
    conditions: {
      nl: "Kinderen en jongeren: 50% van het tarief (max. € 35/sessie). CM vereist een FOD-visum of FOD-erkenning; psychotherapeuten zonder die erkenning komen niet in aanmerking — het platform toont dit automatisch bij je match.",
      fr: "Enfants et jeunes : 50 % du tarif (max. 35 €/séance). La CM exige un visa ou agrément SPF ; les psychothérapeutes sans cet agrément n'entrent pas en ligne de compte — la plateforme l'indique automatiquement lors de votre match.",
      en: "Children and adolescents: 50% of the rate (max €35/session). CM requires an FPS visa or recognition; psychotherapists without it are not eligible — the platform shows this automatically with your match.",
    },
    infoUrl: "https://www.cm.be/diensten-en-voordelen/ziekte-en-behandeling/psychologische-hulp",
  },
  {
    code: "helan",
    name: "Helan Onafhankelijk Ziekenfonds",
    amount: {
      nl: "€ 10/sessie",
      fr: "10 €/séance",
      en: "€10/session",
    },
    cap: {
      nl: "tot 12 sessies per jaar",
      fr: "jusqu'à 12 séances par an",
      en: "up to 12 sessions per year",
    },
    conditions: {
      nl: "Geldt ook voor teleconsultaties (videosessies). Psycholoog, orthopedagoog of psychotherapeut aangesloten bij een beroepsvereniging (het lidmaatschap is de voorwaarde, ook voor psychologen), of seksuoloog aangesloten bij de VVS.",
      fr: "Vaut aussi pour les téléconsultations (séances vidéo). Psychologue, orthopédagogue ou psychothérapeute affilié à une association professionnelle (l'affiliation est la condition, aussi pour les psychologues), ou sexologue affilié à la VVS.",
      en: "Also applies to teleconsultations (video sessions). Psychologist, remedial educationalist or psychotherapist affiliated with a professional association (the membership is the requirement, also for psychologists), or sexologist affiliated with the VVS.",
    },
    infoUrl: "https://www.helan.be",
  },
  {
    code: "solidaris",
    name: "Solidaris",
    amount: {
      nl: "50% van het tarief, max. € 10/sessie (€ 15 bij verhoogde tegemoetkoming); jongeren tot € 20",
      fr: "50 % du tarif, max. 10 €/séance (15 € en intervention majorée) ; jeunes jusqu'à 20 €",
      en: "50% of the rate, max €10/session (€15 with increased allowance); young people up to €20",
    },
    cap: {
      nl: "± 12 sessies per jaar (regionaal verschillend)",
      fr: "± 12 séances par an (variable selon la région)",
      en: "± 12 sessions per year (varies by region)",
    },
    conditions: {
      nl: "Via attest; de hulpverlener-voorwaarden verschillen per regio: Limburg en Oost-Vlaanderen aanvaarden een FOD-visum óf een erkende beroepsvereniging; Antwerpen een vaste verenigingslijst óf Psychologencommissie-inschrijving; Vlaams-Brabant Psychologencommissie óf klinisch psycholoog/orthopedagoog; West-Vlaanderen enkel een door het fonds erkende beroepsvereniging.",
      fr: "Sur attestation ; les conditions varient selon la région : Limbourg et Flandre-Orientale acceptent un visa SPF ou une association professionnelle reconnue ; Anvers une liste fixe d'associations ou l'inscription à la Commission des Psychologues ; Brabant flamand la Commission des Psychologues ou psychologue/orthopédagogue clinicien ; Flandre-Occidentale uniquement une association reconnue par la mutualité.",
      en: "Via certificate; provider conditions vary by region: Limburg and East Flanders accept an FPS visa or a recognised professional association; Antwerp a fixed list of associations or Psychologists' Commission registration; Flemish Brabant the Psychologists' Commission or a clinical psychologist/remedial educationalist; West Flanders only an association recognised by the fund.",
    },
    infoUrl: "https://www.solidaris-vlaanderen.be/terugbetaling-psycholoog",
  },
  {
    code: "lm",
    name: "Liberale Mutualiteit",
    amount: {
      nl: "€ 20/sessie",
      fr: "20 €/séance",
      en: "€20/session",
    },
    cap: {
      nl: "tot € 160 per kalenderjaar",
      fr: "jusqu'à 160 € par année civile",
      en: "up to €160 per calendar year",
    },
    conditions: {
      nl: "Via attest. Erkende psycholoog/orthopedagoog óf psychotherapeut aangesloten bij een beroepsvereniging.",
      fr: "Sur attestation. Psychologue/orthopédagogue agréé ou psychothérapeute affilié à une association professionnelle.",
      en: "Via certificate. Recognised psychologist/remedial educationalist or psychotherapist affiliated with a professional association.",
    },
    infoUrl: "https://www.lm-ml.be",
  },
  {
    code: "nzvl",
    name: "Neutraal Ziekenfonds Vlaanderen",
    amount: {
      nl: "tot € 15/sessie",
      fr: "jusqu'à 15 €/séance",
      en: "up to €15/session",
    },
    cap: {
      nl: "tot 15 sessies per jaar",
      fr: "jusqu'à 15 séances par an",
      en: "up to 15 sessions per year",
    },
    conditions: {
      nl: "Via het eigen NZVL-formulier. Psycholoog bij de Psychologencommissie, psycholoog/orthopedagoog met FOD-visum, VVS-seksuoloog, óf therapeut aangesloten bij de BWP of een daarbij aangesloten vereniging (VVGT, VVPT, BVRGS, VVCEPC) — andere beroepsverenigingen erkent NZVL niet.",
      fr: "Via le formulaire NZVL. Psychologue inscrit à la Commission des Psychologues, psychologue/orthopédagogue avec visa SPF, sexologue VVS, ou thérapeute affilié à la BWP ou à une association qui y est rattachée (VVGT, VVPT, BVRGS, VVCEPC) — les autres associations ne sont pas reconnues par la NZVL.",
      en: "Via the NZVL form. Psychologist registered with the Psychologists' Commission, psychologist/remedial educationalist with an FPS visa, VVS sexologist, or therapist affiliated with the BWP or one of its member associations (VVGT, VVPT, BVRGS, VVCEPC) — other professional associations are not recognised by NZVL.",
    },
  },
  {
    code: "vnz",
    name: "Vlaams & Neutraal Ziekenfonds",
    amount: {
      nl: "€ 10/sessie vanaf 19 jaar; € 20 tot en met 18 jaar",
      fr: "10 €/séance à partir de 19 ans ; 20 € jusqu'à 18 ans",
      en: "€10/session from age 19; €20 up to age 18",
    },
    cap: {
      nl: "tot 15 sessies per kalenderjaar",
      fr: "jusqu'à 15 séances par année civile",
      en: "up to 15 sessions per calendar year",
    },
    conditions: {
      nl: "Via attest. Master psychologie/orthopedagogiek met FOD-visum, inschrijving bij de Psychologencommissie óf psychotherapeut aangesloten bij een beroepsvereniging.",
      fr: "Sur attestation. Master en psychologie/orthopédagogie avec visa SPF, inscription à la Commission des Psychologues ou psychothérapeute affilié à une association professionnelle.",
      en: "Via certificate. Master's in psychology/remedial education with FPS visa, registration with the Psychologists' Commission or psychotherapist affiliated with a professional association.",
    },
    infoUrl: "https://vnz.be/voordelen-terugbetalingen/psycholoog/",
  },
];
